import { createContext, useContext, useState, useMemo, useCallback } from "react";

const ProductsContext = createContext(null);

/**
 * Provider global para productos.
 * Almacena productos con su metadata (almacenes asociados, paginación)
 */
export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalPages: 0,
    totalElements: 0,
    first: true,
    last: false,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);

  //* Setters
  const resetProducts = useCallback((newProducts, paginationData = null) => {
    setProducts(newProducts);
    if (paginationData) {
      setPagination(paginationData);
    }
  }, []);

  const updateProductById = useCallback((productId, updates) => {
    setProducts((prev) =>
      prev.map((product) => (product.id === productId ? { ...product, ...updates } : product))
    );
  }, []);

  const addProduct = useCallback((newProduct) => {
    setProducts((prev) => {
      const exists = prev.some((p) => p.id === newProduct.id);
      return exists ? prev : [...prev, newProduct];
    });
  }, []);

  const removeProduct = useCallback((productId) => {
    setProducts((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  //* Agrega un almacén al array de almacenes de un producto
  const addStoreToProduct = useCallback((productId, storeData) => {
    setProducts((prev) =>
      prev.map((product) => {
        if (product.id !== productId) return product;

        // const currentStores = product.almacenes || [];
        // const exists = currentStores.some((store) => store.id === storeData.id);

        return {
          ...product,
          almacenes: storeData,
          // almacenes: exists ? currentStores : [...currentStores, storeData],
        };
      })
    );
  }, []);

  //* Elimina un almacén del array de almacenes de un producto
  const removeStoreFromProduct = useCallback((productId, storeId) => {
    setProducts((prev) =>
      prev.map((product) => {
        if (product.id !== productId) return product;

        return {
          ...product,
          almacenes: storeId,
          // almacenes: (product.almacenes || []).filter(
          //   (store) => store.id !== storeId
          // ),
        };
      })
    );
  }, []);

  //* Filtrado local
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;

    const term = searchTerm.toLowerCase().trim();
    return products.filter(
      (product) =>
        product.product?.toLowerCase().includes(term) || product.sku?.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  const value = useMemo(
    () => ({
      // Estado
      products,
      filteredProducts,
      pagination,
      searchTerm,
      isSearchMode,

      // Setters directos
      setProducts,
      setPagination,
      setSearchTerm,
      setIsSearchMode,

      // Operaciones primitivas
      resetProducts,
      updateProductById,
      addProduct,
      removeProduct,
      addStoreToProduct,
      removeStoreFromProduct,
    }),
    [
      products,
      filteredProducts,
      pagination,
      searchTerm,
      isSearchMode,
      resetProducts,
      updateProductById,
      addProduct,
      removeProduct,
      addStoreToProduct,
      removeStoreFromProduct,
    ]
  );

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error("useProducts debe usarse dentro de un ProductsProvider");
  }
  return context;
};
